import { useData } from "../contexts/DataContext/DataContextt";
import { useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PageContainer from "../components/Forms/PageContainer";
import { useNavigate } from "react-router";
import { deleteGroup, fetchGroups } from "../services/groupService";
import { groupsPageCache } from "./cache/PageCache";
import { usePagedQuery } from "../contexts/hooks/usePagedQuery";
import TabulatedGridCollapsibleV2 from "../components/Forms/TabulatedGridCollapsibleV2";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { RowActions } from "../utils/RowActions";

let GroupPage = () => {
  const { language } = useData();
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleKeyDown,
    handleSearch,
    data: groupData,
    total,
    searchTerm,
    isLoading,
    reload,
    handleDeleteAction,
    searchType,
    setSearchType,
    searchOption,
  } = usePagedQuery({
    fetchFunction: fetchGroups,
    language,
    searchOption: [
      { id: "groupCode", display: "Code" },
      { id: "groupName", display: "Name" },
    ],
    cache: groupsPageCache,
    deps: [language],
  });

  const headers = [
    /*{
      prop: [
        { id: "groupDisplayName" },
        { id: "groupCode" },
        { id: "description" },
      ],
      text: "Group Info",
    }*/
    {
      prop: "groupCode",
      text: "Code",
      width: 160,
      link: {
        action: (row) =>
          navigate(`/group/edit/${row.groupCode}`, { state: { row } }),
      },
    },
    {
      prop: "groupName",
      text: "Name",
    },
    {
      prop: "groupDisplayName",
      text: "Display Name",
    },
  ];

  const pageRowActions = [
    {
      tooltip: "Edit Group",
      icon: <EditNoteRoundedIcon />,
      onClick: (row) =>
        navigate(`/group/edit/${row.groupCode}`, { state: { row } }),
    },
    {
      tooltip: "Delete Group",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: (row) => {
        handleDeleteAction(deleteGroup, row, "groupCode", {
          title: "Delete Group",
          message: `Are you sure you want to delete this Group? ${row.groupCode}`,
        });
      },
    },
  ];

  const handleCreateClick = useCallback(() => {
    navigate(`/groups/new`);
  }, [navigate]);

  return (
    <PageContainer
      title="ETIM Groups"
      subtitle="Manage ETIM product groups and their classes"
      titleAdornment={
        <Chip
          size="small"
          label={total}
          aria-label={`${total} ${searchTerm ? "matching " : ""}groups`}
        />
      }
      breadcrumbs={[{ title: "ETIM Groups" }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip
            title="Refresh Etim Group"
            placement="bottom"
            enterDelay={300}
          >
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={() => {
                  reload({ force: true });
                }}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box mt={3}>
        <TabulatedGridCollapsibleV2
          variant="modern"
          loading={isLoading}
          emptyState={
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, color: "text.secondary" }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography>No groups found</Typography>
              {searchTerm && (
                <Button size="small" onClick={() => handleSearch("")}>
                  Clear search
                </Button>
              )}
            </Stack>
          }
          pagination={{
            totalRows: total,
            page: page,
            rowsPerPage: rowsPerPage,
            handleChangePage: handleChangePage,
            handleChangeRowsPerPage: handleChangeRowsPerPage,
          }}
          search={{
            placeHolder: "Search...",
            searchOption: searchOption,
            searchType: searchType,
            setSearchType: setSearchType,
            handleSearchClick: handleSearch,
            onKeyDown: handleKeyDown,
            activeTerm: searchTerm,
            onClear: () => handleSearch(""),
          }}
          data={groupData}
          headers={headers}
          rowActions={(row) => (
            <RowActions row={row} actions={pageRowActions} />
          )}
        />
      </Box>
    </PageContainer>
  );
};

export default GroupPage;
